import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Typography,
  Button,
  Dropdown,
  Avatar,
  theme,
  Drawer,
  Grid,
  Flex,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  MenuOutlined,
  LogoutOutlined,
  UserOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { set_is_mobile_sidebar } from '../../../core/data/redux/action';
import { useDispatch } from 'react-redux';
import { all_routes } from '../../../core/data/routes/all_routes';
import { adminSidebar } from '../../../core/data/json/admin_sidebar_data';

const { Header: AntHeader } = Layout;
const { useBreakpoint } = Grid;

type StoredUser = {
  avatar?: string;
  avatar_url?: string;
  profileImage?: string;
  profile_image?: string;
  profilePhoto?: string;
  profile_photo?: string;
  image?: string;
  photo?: string;
  photo_url?: string;
  full_name?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  [key: string]: unknown;
};

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const routes = all_routes;
  const screens = useBreakpoint();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<StoredUser | null>(null);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userData');
    setCurrentUser(null);
    navigate('/auth/login');
  };

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
    },
  ];

  const onUserMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    }
  };

  const mobileMenuSections = useMemo(() => {
    return adminSidebar.map((section) => ({
      title: section.tittle,
      items: section.menu,
    }));
  }, []);

  const handleMenuButtonClick = () => {
    if (screens.lg) {
      dispatch(set_is_mobile_sidebar(true));
    } else {
      setIsDrawerOpen(true);
    }
  };

  const handleNavigate = (path?: string) => {
    if (path) {
      navigate(path);
      setIsDrawerOpen(false);
    }
  };

  useEffect(() => {
    if (screens.lg) {
      setIsDrawerOpen(false);
    }
  }, [screens.lg]);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('userData');
      if (storedUser) {
        setCurrentUser(JSON.parse(storedUser));
      } else {
        setCurrentUser(null);
      }
    } catch {
      setCurrentUser(null);
    }
  }, []);

  const userAvatarSrc =
    currentUser?.avatar ||
    currentUser?.avatar_url ||
    currentUser?.profileImage ||
    currentUser?.profile_image ||
    currentUser?.profilePhoto ||
    currentUser?.profile_photo ||
    currentUser?.image ||
    currentUser?.photo ||
    currentUser?.photo_url ||
    '';

  const userDisplayName = useMemo(() => {
    if (!currentUser) {
      return '';
    }
    if (currentUser.full_name) return currentUser.full_name;
    if (currentUser.name) return currentUser.name;
    const first = currentUser.first_name || '';
    const last = currentUser.last_name || '';
    return `${first} ${last}`.trim();
  }, [currentUser]);

  const avatarInitials = useMemo(() => {
    if (userAvatarSrc || !userDisplayName) {
      return '';
    }
    const parts = userDisplayName.split(' ').filter(Boolean);
    if (!parts.length) {
      return '';
    }
    const [first, second] = [parts[0], parts[parts.length - 1]];
    const initials = `${first?.[0] || ''}${parts.length > 1 ? second?.[0] || '' : ''}`;
    return initials.toUpperCase();
  }, [userAvatarSrc, userDisplayName]);

  return (
    <AntHeader
      className="admin-header ant-admin-header"
      style={{
        background: colorBgContainer,
        padding: '0 24px',
      }}
    >
      <Flex className="ant-admin-header__content" align="center" justify="space-between">
        <Flex align="center" justify='start' gap={12} className="ant-admin-header__left" wrap="nowrap" style={{marginLeft: 30}}>
          <Button
            type="text"
            id="mobile_btn"
            aria-label="Toggle sidebar"
            icon={<MenuOutlined />}
            className="ant-admin-header__mobile-btn mobile_btn"
            onClick={handleMenuButtonClick}
          />
          <Link to={routes.dashboard} className="ant-admin-header__brand">
            <Typography.Title level={4} className="ant-admin-header__brand-title">
              Chozen Admin
            </Typography.Title>
          </Link>
          <div>
            <Dropdown
              menu={{ items: userMenuItems, onClick: onUserMenuClick }}
              placement="bottomRight"
              trigger={['click']}
            >
              <Button
                type="text"
                aria-label="Open user menu"
                className="ant-admin-header__user"
                title={userDisplayName || 'Open user menu'}
              >
                <Avatar
                  size={40}
                  src={userAvatarSrc || undefined}
                  alt={userDisplayName || 'User avatar'}
                  style={{
                    backgroundColor: userAvatarSrc ? '#e6ebf5' : '#8c8c8c',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {!userAvatarSrc && (avatarInitials || <UserOutlined />)}
                </Avatar>
              </Button>
            </Dropdown>
          </div>
        </Flex>
        
      </Flex>
      <Drawer
        placement="left"
        width={300}
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        bodyStyle={{ padding: 0 }}
        headerStyle={{ display: 'none' }}
        className="ant-admin-mobile-sidebar"
      >
        <div className="ant-admin-mobile-sidebar__header">
          <Typography.Title level={4}>Chozen Admin</Typography.Title>
          <Button type="text" onClick={() => setIsDrawerOpen(false)}>
            Close
          </Button>
        </div>
        <div className="ant-admin-mobile-sidebar__content">
          {mobileMenuSections.map((section) => (
            <div className="ant-admin-mobile-sidebar__section" key={section.title}>
              <Typography.Text type="secondary" className="ant-admin-mobile-sidebar__section-title">
                {section.title}
              </Typography.Text>
              <div className="ant-admin-mobile-sidebar__menu">
                {section.items.map((menuItem) => {
                  if (menuItem.hasSubRoute && menuItem.subMenus?.length) {
                    return (
                      <div className="ant-admin-mobile-sidebar__submenu" key={menuItem.menuValue}>
                        <Typography.Text className="ant-admin-mobile-sidebar__submenu-title">
                          {menuItem.menuValue}
                        </Typography.Text>
                        {menuItem.subMenus.map((subMenu) => (
                          <Button
                            key={subMenu.menuValue}
                            type="text"
                            block
                            className={`ant-admin-mobile-sidebar__menu-item ${
                              location.pathname === subMenu.route ? 'active' : ''
                            }`}
                            icon={<RightOutlined />}
                            onClick={() => handleNavigate(subMenu.route)}
                          >
                            {subMenu.menuValue}
                          </Button>
                        ))}
                      </div>
                    );
                  }
                  return (
                    <Button
                      key={menuItem.menuValue}
                      type="text"
                      block
                      className={`ant-admin-mobile-sidebar__menu-item ${
                        location.pathname === menuItem.route ? 'active' : ''
                      }`}
                      icon={<RightOutlined />}
                      onClick={() => handleNavigate(menuItem.route)}
                    >
                      {menuItem.menuValue}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </Drawer>
    </AntHeader>
  );
};

export default Header;
